import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchContractComments, addContractComment } from '../../store/slices/contractsSlice';
import type { RootState, AppDispatch } from '../../store';
import type {
  Contract,
  ContractComment,
  CreateContractCommentData,
  CommentVisibility,
} from '../../types/contracts';
import { COMMENT_VISIBILITY_OPTIONS } from '../../types/contracts';

interface ContractCommentsProps {
  contract: Contract;
}

const ContractComments: React.FC<ContractCommentsProps> = ({ contract }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { comments, commentsLoading } = useSelector((state: RootState) => state.contracts);

  const [newComment, setNewComment] = useState<CreateContractCommentData>({
    content: '',
    visibility: 'all',
    mentions: [],
    attachments: [],
  });
  const [replyToComment, setReplyToComment] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchContractComments({ contractId: contract.id }));
  }, [dispatch, contract.id]);

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newComment.content.trim()) return;

    try {
      await dispatch(
        addContractComment({
          contractId: contract.id,
          comment: {
            ...newComment,
            parentCommentId: replyToComment,
          },
        })
      ).unwrap();

      setNewComment({
        content: '',
        visibility: 'all',
        mentions: [],
        attachments: [],
      });
      setReplyToComment(null);
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };

  const handleReply = (commentId: string) => {
    setReplyToComment(commentId);
  };

  const formatDate = (date: string | Date) => {
    const now = new Date();
    const commentDate = new Date(date);
    const diffInSeconds = (now.getTime() - commentDate.getTime()) / 1000;
    const diffInMinutes = diffInSeconds / 60;
    const diffInHours = diffInMinutes / 60;
    const diffInDays = diffInHours / 24;

    if (diffInSeconds < 60) {
      return 'just now';
    } else if (diffInMinutes < 60) {
      return `${Math.floor(diffInMinutes)}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else if (diffInDays < 7) {
      return `${Math.floor(diffInDays)}d ago`;
    } else {
      return commentDate.toLocaleDateString();
    }
  };

  const getVisibilityColor = (visibility: CommentVisibility) => {
    switch (visibility) {
      case 'internal':
        return 'blue';
      case 'external':
        return 'green';
      default:
        return 'gray';
    }
  };

  const getVisibilityIcon = (visibility: CommentVisibility) => {
    switch (visibility) {
      case 'internal':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
        );
      case 'external':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
            />
          </svg>
        );
    }
  };

  // Group comments by parent (for threading)
  const topLevelComments = comments.filter(comment => !comment.parentCommentId);
  const replies = comments.filter(comment => comment.parentCommentId);
  const getRepliesForComment = (commentId: string) =>
    replies.filter(reply => reply.parentCommentId === commentId);

  if (commentsLoading && comments.length === 0) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex space-x-3">
              <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                <div className="h-16 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Discussion</h3>
          <p className="text-sm text-gray-600 mt-1">
            {comments.length} comment{comments.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* New Comment Form */}
      <div className="mb-8">
        <form onSubmit={handleSubmitComment} className="space-y-4">
          {replyToComment && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-blue-800">Replying to comment</span>
                <button
                  type="button"
                  onClick={() => setReplyToComment(null)}
                  className="text-blue-400 hover:text-blue-600"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}

          <div>
            <textarea
              value={newComment.content}
              onChange={e => setNewComment(prev => ({ ...prev, content: e.target.value }))}
              rows={4}
              placeholder="Add a comment..."
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange resize-none"
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Visibility</label>
                <select
                  value={newComment.visibility}
                  onChange={e =>
                    setNewComment(prev => ({
                      ...prev,
                      visibility: e.target.value as CommentVisibility,
                    }))
                  }
                  className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-brand-orange focus:border-brand-orange"
                >
                  {COMMENT_VISIBILITY_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-600 text-sm"
                  title="Attach file"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-600 text-sm"
                  title="Mention someone"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={!newComment.content.trim()}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-orange hover:bg-brand-orange-dark disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {replyToComment ? 'Reply' : 'Comment'}
            </button>
          </div>
        </form>
      </div>

      {/* Comments List */}
      <div className="space-y-6">
        {topLevelComments.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="w-16 h-16 text-gray-400 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No comments yet</h3>
            <p className="text-gray-500 mb-4">Start the discussion by adding a comment</p>
          </div>
        ) : (
          topLevelComments.map(comment => (
            <CommentThread
              key={comment.id}
              comment={comment}
              replies={getRepliesForComment(comment.id)}
              onReply={handleReply}
              formatDate={formatDate}
              getVisibilityColor={getVisibilityColor}
              getVisibilityIcon={getVisibilityIcon}
            />
          ))
        )}
      </div>
    </div>
  );
};

// Comment Thread Component
interface CommentThreadProps {
  comment: ContractComment;
  replies: ContractComment[];
  onReply: (commentId: string) => void;
  formatDate: (date: string | Date) => string;
  getVisibilityColor: (visibility: CommentVisibility) => string;
  getVisibilityIcon: (visibility: CommentVisibility) => JSX.Element;
}

const CommentThread: React.FC<CommentThreadProps> = ({
  comment,
  replies,
  onReply,
  formatDate,
  getVisibilityColor,
  getVisibilityIcon,
}) => {
  const [showReplies, setShowReplies] = useState(true);

  return (
    <div className="space-y-4">
      {/* Main Comment */}
      <div className="flex space-x-3">
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-brand-orange rounded-full flex items-center justify-center text-white text-sm font-medium">
            {comment.authorName.charAt(0).toUpperCase()}
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center space-x-2">
                <span className="font-medium text-gray-900">{comment.authorName}</span>
                <span className="text-xs text-gray-500">{formatDate(comment.createdAt)}</span>
                {comment.isEdited && <span className="text-xs text-gray-400">(edited)</span>}
              </div>

              <div className="flex items-center space-x-2">
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-${getVisibilityColor(comment.visibility)}-100 text-${getVisibilityColor(comment.visibility)}-800`}
                >
                  {getVisibilityIcon(comment.visibility)}
                  <span className="ml-1">{comment.visibility}</span>
                </span>
                <button className="text-gray-400 hover:text-gray-600">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="text-gray-900 whitespace-pre-wrap">{comment.content}</div>

            {comment.attachments && comment.attachments.length > 0 && (
              <div className="mt-3 space-y-2">
                {comment.attachments.map((attachment, index) => (
                  <div key={index} className="flex items-center space-x-2 text-sm text-blue-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                      />
                    </svg>
                    <a
                      href={attachment.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      {attachment.fileName}
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center space-x-4 mt-2 text-sm">
            <button
              onClick={() => onReply(comment.id)}
              className="text-gray-500 hover:text-gray-700 flex items-center space-x-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                />
              </svg>
              <span>Reply</span>
            </button>

            {replies.length > 0 && (
              <button
                onClick={() => setShowReplies(!showReplies)}
                className="text-gray-500 hover:text-gray-700 flex items-center space-x-1"
              >
                <svg
                  className={`w-4 h-4 transform transition-transform ${showReplies ? 'rotate-90' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                <span>
                  {replies.length} repl{replies.length === 1 ? 'y' : 'ies'}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Replies */}
      {showReplies && replies.length > 0 && (
        <div className="ml-11 space-y-4">
          {replies.map(reply => (
            <div key={reply.id} className="flex space-x-3">
              <div className="flex-shrink-0">
                <div className="w-6 h-6 bg-gray-400 rounded-full flex items-center justify-center text-white text-xs font-medium">
                  {reply.authorName.charAt(0).toUpperCase()}
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="bg-white border border-gray-200 rounded-lg p-3">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-900 text-sm">{reply.authorName}</span>
                      <span className="text-xs text-gray-500">{formatDate(reply.createdAt)}</span>
                      {reply.isEdited && <span className="text-xs text-gray-400">(edited)</span>}
                    </div>

                    <span
                      className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-${getVisibilityColor(reply.visibility)}-100 text-${getVisibilityColor(reply.visibility)}-800`}
                    >
                      {getVisibilityIcon(reply.visibility)}
                    </span>
                  </div>

                  <div className="text-gray-900 text-sm whitespace-pre-wrap">{reply.content}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContractComments;
